/**
 * Error Tracking Service
 * 
 * Frontend'de oluşan hataları otomatik olarak yakalar ve backend'e raporlar.
 * 
 * Özellikler:
 * - Global error handler
 * - Unhandled promise rejection handler
 * - React Error Boundary entegrasyonu
 * - Console.error interceptor
 * - Click/interaction error detection
 * - Performance issues tracking
 */

class ErrorTracker {
    constructor() {
        this.initialized = false;
        this.errorQueue = [];
        this.flushInterval = null;
        this.sessionId = this.generateSessionId();
        this.errorCount = 0;
        this.maxErrorsPerSession = 100; // Spam önleme
        this.recentErrors = new Set(); // Duplicate önleme
        this.apiEndpoint = '/api/errors/report';
    }

    /**
     * Session ID oluştur
     */
    generateSessionId() {
        return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    }

    /**
     * Error tracker'ı başlat
     */
    init(options = {}) {
        if (this.initialized) return;

        this.apiEndpoint = options.apiEndpoint || this.apiEndpoint;
        this.maxErrorsPerSession = options.maxErrors || this.maxErrorsPerSession;

        // Global error handler
        this.setupGlobalErrorHandler();

        // Promise rejection handler
        this.setupPromiseRejectionHandler();

        // Console.error interceptor
        if (options.interceptConsole !== false) {
            this.setupConsoleInterceptor();
        }

        // Click error detection
        if (options.trackClickErrors !== false) {
            this.setupClickErrorDetection();
        }

        // Periyodik flush
        this.flushInterval = setInterval(() => this.flush(), 5000);

        this.initialized = true;
        console.log('[ErrorTracker] Initialized');
    }

    /**
     * Global window.onerror handler
     */
    setupGlobalErrorHandler() {
        window.onerror = (message, source, lineno, colno, error) => {
            this.captureError({
                type: 'runtime',
                message: message?.toString() || 'Unknown error',
                source,
                lineno,
                colno,
                stack: error?.stack,
                error: error?.toString()
            });
            return false; // Hata konsolda da görünsün
        };
    }

    /**
     * Unhandled promise rejection handler
     */
    setupPromiseRejectionHandler() {
        window.onunhandledrejection = (event) => {
            const error = event.reason;
            this.captureError({
                type: 'promise_rejection',
                message: error?.message || error?.toString() || 'Unhandled promise rejection',
                stack: error?.stack,
                error: error?.toString()
            });
        };
    }

    /**
     * Console.error interceptor
     */
    setupConsoleInterceptor() {
        const originalError = console.error;
        const tracker = this;

        console.error = function (...args) {
            // Orijinal davranışı koru
            originalError.apply(console, args);

            // Hata yakala
            const message = args.map(arg => {
                if (arg instanceof Error) {
                    return arg.message;
                }
                if (typeof arg === 'object') {
                    try {
                        return JSON.stringify(arg);
                    } catch {
                        return String(arg);
                    }
                }
                return String(arg);
            }).join(' ');

            // React internal hatalarını filtrele
            if (message.includes('Warning:') || message.includes('React does not recognize')) {
                return;
            }

            tracker.captureError({
                type: 'console_error',
                message: message.substring(0, 1000),
                stack: args[0]?.stack
            });
        };
    }

    /**
     * Click error detection - Çalışmayan butonları tespit et
     */
    setupClickErrorDetection() {
        let lastClickTarget = null;
        let lastClickTime = 0;
        let clickCount = 0;

        document.addEventListener('click', (event) => {
            const target = event.target;
            const now = Date.now();

            // Aynı elemente kısa sürede çoklu tıklama tespiti
            if (lastClickTarget === target && now - lastClickTime < 500) {
                clickCount++;

                // 3'ten fazla hızlı tıklama = muhtemelen çalışmayan buton
                if (clickCount >= 3) {
                    const element = target.closest('button, a, [role="button"], [onclick]');
                    if (element) {
                        this.captureError({
                            type: 'ui_interaction',
                            subtype: 'unresponsive_element',
                            message: `Element çalışmıyor olabilir: ${this.getElementDescription(element)}`,
                            element: {
                                tag: element.tagName,
                                id: element.id,
                                class: element.className,
                                text: element.textContent?.substring(0, 50),
                                disabled: element.disabled,
                                href: element.href
                            },
                            pageUrl: window.location.href,
                            clickCount
                        });
                    }
                    clickCount = 0;
                }
            } else {
                clickCount = 1;
            }

            lastClickTarget = target;
            lastClickTime = now;
        }, true);

        // Form submission hatalarını yakala
        document.addEventListener('submit', (event) => {
            const form = event.target;

            // Validation hatalarını kontrol et
            setTimeout(() => {
                const invalidFields = form.querySelectorAll(':invalid');
                if (invalidFields.length > 0) {
                    this.captureError({
                        type: 'ui_interaction',
                        subtype: 'form_validation',
                        message: `Form validation hatası: ${invalidFields.length} alan`,
                        severity: 'info',
                        form: {
                            id: form.id,
                            action: form.action,
                            invalidFields: Array.from(invalidFields).map(f => ({
                                name: f.name,
                                type: f.type,
                                validationMessage: f.validationMessage
                            }))
                        }
                    });
                }
            }, 100);
        }, true);
    }

    /**
     * Element açıklaması oluştur
     */
    getElementDescription(element) {
        const parts = [element.tagName.toLowerCase()];
        if (element.id) parts.push(`#${element.id}`);
        if (element.className) parts.push(`.${element.className.split(' ')[0]}`);
        const text = element.textContent?.trim().substring(0, 20);
        if (text) parts.push(`"${text}..."`);
        return parts.join('');
    }

    /**
     * Hata fingerprint oluştur (duplicate tespiti için)
     */
    getErrorFingerprint(error) {
        const key = `${error.type}|${error.message?.substring(0, 100)}|${error.source}|${error.lineno}`;
        // Türkçe karakterler için encodeURIComponent kullan (btoa Latin1 dışı karakterleri desteklemiyor)
        try {
            return btoa(encodeURIComponent(key)).substring(0, 16);
        } catch (e) {
            // Fallback: basit hash oluştur
            let hash = 0;
            for (let i = 0; i < key.length; i++) {
                const char = key.charCodeAt(i);
                hash = ((hash << 5) - hash) + char;
                hash = hash & hash;
            }
            return Math.abs(hash).toString(36).substring(0, 16);
        }
    }

    /**
     * Hata yakala
     */
    captureError(errorData) {
        // Limit kontrolü
        if (this.errorCount >= this.maxErrorsPerSession) {
            return;
        }

        // Duplicate kontrolü
        const fingerprint = this.getErrorFingerprint(errorData);
        if (this.recentErrors.has(fingerprint)) {
            return;
        }
        this.recentErrors.add(fingerprint);

        // 5 dakika sonra fingerprint'i temizle (aynı hata tekrar raporlanabilsin)
        setTimeout(() => this.recentErrors.delete(fingerprint), 5 * 60 * 1000);

        const error = {
            ...errorData,
            timestamp: new Date().toISOString(),
            sessionId: this.sessionId,
            userAgent: navigator.userAgent,
            pageUrl: window.location.href,
            referrer: document.referrer,
            screenSize: `${window.innerWidth}x${window.innerHeight}`,
            language: navigator.language
        };

        this.errorQueue.push(error);
        this.errorCount++;

        // Kritik hatalar hemen gönderilsin
        if (errorData.type === 'runtime' || errorData.type === 'promise_rejection') {
            this.flush();
        }
    }

    /**
     * React Error Boundary'den hata al
     */
    captureReactError(error, errorInfo) {
        this.captureError({
            type: 'react_error',
            message: error?.message || error?.toString() || 'React error',
            stack: error?.stack,
            componentStack: errorInfo?.componentStack
        });
    }

    /**
     * Manuel hata raporu
     */
    reportError(message, extra = {}) {
        this.captureError({
            type: 'manual_report',
            message,
            ...extra
        });
    }

    /**
     * Kullanıcı feedback'i
     */
    reportFeedback(description, category = 'bug') {
        this.captureError({
            type: 'user_feedback',
            subtype: category,
            message: description,
            severity: 'info'
        });
        this.flush(); // Hemen gönder
    }

    /**
     * Hataları backend'e gönder
     */
    async flush() {
        if (this.errorQueue.length === 0) return;

        const errors = [...this.errorQueue];
        this.errorQueue = [];

        for (const error of errors) {
            try {
                await fetch(this.apiEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify(error)
                });
            } catch (e) {
                // Network hatası durumunda kuyruğa geri ekle
                this.errorQueue.push(error);
                console.warn('[ErrorTracker] Failed to send error:', e.message);
                break; // Diğerlerini deneme
            }
        }
    }

    /**
     * Temizlik
     */
    destroy() {
        if (this.flushInterval) {
            clearInterval(this.flushInterval);
        }
        this.flush();
        this.initialized = false;
    }
}

// Singleton instance
const errorTracker = new ErrorTracker();

// React Error Boundary için helper
export function captureReactError(error, errorInfo) {
    errorTracker.captureReactError(error, errorInfo);
}

// Manuel hata raporu için
export function reportError(message, extra = {}) {
    errorTracker.reportError(message, extra);
}

// Kullanıcı feedback için
export function reportFeedback(description, category = 'bug') {
    errorTracker.reportFeedback(description, category);
}

// Initialize
export function initErrorTracker(options = {}) {
    errorTracker.init(options);
}

export default errorTracker;
